import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Loader2, Sparkles } from "lucide-react";
import {
  SKILL_FOCUS_OPTIONS,
  TIME_OPTIONS,
} from "../lib/options";
import { SkillFocusPicker } from "./SkillFocusPicker";

const fieldClass =
  "bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus:ring-2 focus:ring-[#96ded1] focus-visible:ring-[#96ded1] rounded-md h-12";

/**
 * FocusAreaGroup — store-driven look-for picker for a single skill.
 * Labels are look-for names, values are look-for numbers. The cap is
 * enforced by the parent (atCap) across all groups combined.
 */
const FocusAreaGroup = ({ skillName, focusAreas, selected, onToggle, atCap, testidPrefix }) => (
  <div className="mt-5">
    <Label className="font-bold uppercase tracking-[0.15em] text-xs text-[#4a6984]/70">
      Focus areas inside {skillName} (up to two total)
    </Label>
    <div
      className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2"
      data-testid={`${testidPrefix}-subcomponents`}
    >
      {focusAreas.map((lf) => {
        const checked = selected.includes(lf.number);
        const disabled = !checked && atCap;
        return (
          <button
            key={lf.number}
            type="button"
            onClick={() => onToggle(lf.number)}
            disabled={disabled}
            data-testid={`${testidPrefix}-lookfor-${lf.number}`}
            className={`text-left rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
              checked
                ? "border-[#96ded1] bg-[#96ded1]/15 text-[#1c2d3d]"
                : disabled
                ? "border-[#4a6984]/10 bg-white text-[#4a6984]/40 cursor-not-allowed"
                : "border-[#4a6984]/15 bg-white text-[#4a6984] hover:border-[#4a6984]/40"
            }`}
          >
            <span className="font-bold">{lf.name}</span>
            {lf.use_when ? (
              <span
                className="mt-1 block text-xs font-normal leading-snug text-[#4a6984]/70"
                data-testid={`${testidPrefix}-lookfor-use-when-${lf.number}`}
              >
                {lf.use_when}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  </div>
);

// Skills Generator uses grade DIVISION (developmentally accurate for
// transferable skills) instead of a single precise grade.
const DIVISION_OPTIONS = [
  { value: "junior", label: "Junior (Grades 4–6)" },
  { value: "intermediate", label: "Intermediate (Grades 7–8)" },
];

/**
 * SkillsGeneratorForm — focused skill-first variant of the main
 * generator. Skill-only mode: subject-free. Backend sees `skill_only:true`
 * so Claude generates a pure transferable-skill activity instead of
 * binding to a curriculum subject.
 *
 * Fields:
 *   - primary skill (required) + sub-components (optional)
 *   - secondary skill (optional)
 *   - grade (required)
 *   - time (optional, no preselection — the teacher picks)
 *   - context prompt (optional)
 */
export const SkillsGeneratorForm = ({ onGenerate, loading }) => {
  const [primary, setPrimary] = useState("");
  const [primaryLookfors, setPrimaryLookfors] = useState([]); // look-for numbers
  const [focusStore, setFocusStore] = useState(null);
  const [secondary, setSecondary] = useState("");
  const [secondaryLookfors, setSecondaryLookfors] = useState([]); // look-for numbers
  const [division, setDivision] = useState("");
  const [time, setTime] = useState("");
  const [prompt, setPrompt] = useState("");
  // Phase 92 — opt-in Community sharing.
  const [shareToCommunity, setShareToCommunity] = useState(false);
  const [communityDisplayName, setCommunityDisplayName] = useState("");

  // Allow Skills Library / SITW handoffs to pre-fill the primary skill.
  useEffect(() => {
    const pickUp = () => {
      try {
        const seeded = sessionStorage.getItem("atomicedu_prefill_skill_focus");
        if (seeded && SKILL_FOCUS_OPTIONS.includes(seeded)) {
          setPrimary(seeded);
          sessionStorage.removeItem("atomicedu_prefill_skill_focus");
        }
      } catch (_e) {
        /* sessionStorage blocked */
      }
    };
    pickUp();
    window.addEventListener("focus", pickUp);
    return () => window.removeEventListener("focus", pickUp);
  }, []);

  // Store-driven focus areas (labels + numbers) fetched once from the
  // verified look-for store. No focus-area wording is hardcoded here.
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/skills/focus-areas`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setFocusStore)
      .catch(() => setFocusStore(null));
  }, []);

  const resolveSkillId = (title) => {
    if (!focusStore || !title) return null;
    const t = title.trim().toLowerCase();
    for (const [sid, s] of Object.entries(focusStore)) {
      if ((s.skill_name || "").toLowerCase() === t) return sid;
    }
    for (const [sid, s] of Object.entries(focusStore)) {
      const first = (s.skill_name || "").toLowerCase().split(/[ ,&]/)[0];
      if (first && t.includes(first)) return sid;
    }
    return null;
  };
  const primarySkillId = resolveSkillId(primary);
  const secondarySkillId = resolveSkillId(secondary);
  const primaryFocusAreas =
    focusStore && primarySkillId ? focusStore[primarySkillId].lookfors || [] : [];
  const secondaryFocusAreas =
    focusStore && secondarySkillId ? focusStore[secondarySkillId].lookfors || [] : [];

  // Cap is TWO focus areas TOTAL across both skills combined.
  const totalSelected = primaryLookfors.length + secondaryLookfors.length;
  const atCap = totalSelected >= 2;

  // Reset selections when the relevant skill changes.
  useEffect(() => {
    setPrimaryLookfors([]);
  }, [primary]);
  useEffect(() => {
    setSecondaryLookfors([]);
  }, [secondary]);

  const makeToggle = (setter) => (num) =>
    setter((prev) =>
      prev.includes(num)
        ? prev.filter((n) => n !== num)
        : atCap
        ? prev
        : [...prev, num]
    );
  const togglePrimaryLookfor = makeToggle(setPrimaryLookfors);
  const toggleSecondaryLookfor = makeToggle(setSecondaryLookfors);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!primary || !division) return;

    const secondaryWeave = secondary
      ? `Also weave in ${secondary} as a secondary transferable skill — students should practise both skills through the same activity. `
      : "";
    const composedPrompt = `${secondaryWeave}${prompt}`.trim();

    onGenerate({
      skill_focus: primary,
      // Submit look-for NUMBERS (never labels) so a wording change never
      // breaks a saved selection. Max two TOTAL across both skills.
      skill_lookfor_selections: [
        ...(primarySkillId
          ? primaryLookfors.map((n) => ({ skill_id: primarySkillId, lookfor_number: n }))
          : []),
        ...(secondarySkillId
          ? secondaryLookfors.map((n) => ({ skill_id: secondarySkillId, lookfor_number: n }))
          : []),
      ],
      division,
      time,
      prompt: composedPrompt,
      activity_type: "Lesson",
      // Skill-only mode — Claude generates a pure transferable-skill
      // activity, decoupled from any specific curriculum subject.
      skill_only: true,
      output_type: "lesson",
      share_to_community: shareToCommunity,
      community_display_name: shareToCommunity ? communityDisplayName.trim() : "",
    });
  };

  const secondaryOptions = SKILL_FOCUS_OPTIONS.filter((s) => s !== primary);
  const canSubmit = primary && division && !loading;

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="skills-generator-form"
      className="ae-card p-6 md:p-8 shadow-md"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Primary skill — Tier 2 #4: shared SkillFocusPicker with
            per-skill descriptors + "Let Atomic Edu choose" sentinel. */}
        <SkillFocusPicker
          value={primary}
          onChange={setPrimary}
          testid="skills-generator-primary"
          label="Primary Transferable Skill"
          triggerClassName="mt-2 h-12"
        />

        {/* Secondary skill */}
        <div>
          <Label className="font-bold uppercase tracking-[0.15em] text-xs text-[#4a6984]/70">
            Secondary Skill (optional)
          </Label>
          <Select
            value={secondary}
            onValueChange={(v) => setSecondary(v === "__none" ? "" : v)}
            disabled={!primary}
          >
            <SelectTrigger
              data-testid="skills-generator-secondary"
              className={`${fieldClass} mt-2`}
            >
              <SelectValue
                placeholder={
                  primary ? "Add a second skill" : "Pick a primary skill first"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">None</SelectItem>
              {secondaryOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Focus areas — store-driven, one group per selected skill, labels
          are look-for names, values are look-for numbers, capped at two
          TOTAL across both skills. */}
      {primaryFocusAreas.length > 0 && (
        <FocusAreaGroup
          skillName={primary}
          focusAreas={primaryFocusAreas}
          selected={primaryLookfors}
          onToggle={togglePrimaryLookfor}
          atCap={atCap}
          testidPrefix="skills-generator"
        />
      )}
      {secondary && secondaryFocusAreas.length > 0 && (
        <FocusAreaGroup
          skillName={secondary}
          focusAreas={secondaryFocusAreas}
          selected={secondaryLookfors}
          onToggle={toggleSecondaryLookfor}
          atCap={atCap}
          testidPrefix="skills-generator-secondary"
        />
      )}

      {/* Division + Time (subject removed — Skills Generator is skill-only) */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label className="font-bold uppercase tracking-[0.15em] text-xs text-[#4a6984]/70">
            Grade Division *
          </Label>
          <Select value={division} onValueChange={setDivision}>
            <SelectTrigger
              data-testid="skills-generator-division"
              className={`${fieldClass} mt-2`}
            >
              <SelectValue placeholder="Select division" />
            </SelectTrigger>
            <SelectContent>
              {DIVISION_OPTIONS.map((d) => (
                <SelectItem key={d.value} value={d.value} data-testid={`skills-generator-division-${d.value}`}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-bold uppercase tracking-[0.15em] text-xs text-[#4a6984]/70">
            Time
          </Label>
          <Select value={time} onValueChange={setTime}>
            <SelectTrigger
              data-testid="skills-generator-time"
              className={`${fieldClass} mt-2`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Context prompt */}
      <div className="mt-5">
        <Label className="font-bold uppercase tracking-[0.15em] text-xs text-[#4a6984]/70">
          Describe your class or context (optional)
        </Label>
        <Textarea
          data-testid="skills-generator-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g. Students struggle to explain their reasoning out loud."
          rows={3}
          className={`${fieldClass} mt-2 !h-auto py-3`}
        />
      </div>

      <div className="mt-6 rounded-xl border border-[#96ded1]/40 bg-[#96ded1]/10 px-4 py-3 sm:px-5 sm:py-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="skills-share-to-community-checkbox"
            checked={shareToCommunity}
            onChange={(e) => setShareToCommunity(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-[#1c2d3d]/30 text-[#1c2d3d] focus:ring-2 focus:ring-[#96ded1]"
          />
          <span className="flex-1">
            <span className="block font-extrabold text-[#1c2d3d] text-sm">
              Share this lesson with the Atomic Edu community
            </span>
            <span className="block text-xs text-[#1c2d3d]/70 mt-0.5 font-medium">
              Your name, personal notes, and class details are kept private.
              Anyone on a paid plan can preview and import a copy.
            </span>
          </span>
        </label>
        {shareToCommunity && (
          <div className="mt-3 pl-7">
            <Label
              htmlFor="skills-community-display-name"
              className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#4a6984]/70"
            >
              Display name (optional)
            </Label>
            <input
              id="skills-community-display-name"
              type="text"
              data-testid="skills-community-display-name-input"
              value={communityDisplayName}
              onChange={(e) => setCommunityDisplayName(e.target.value.slice(0, 60))}
              placeholder={`e.g. "Ms. P" or "Grade 5 Teacher in Simcoe County"`}
              className={`${fieldClass} mt-1 max-w-md w-full px-3 py-2 outline-none`}
              maxLength={60}
            />
            <p className="text-[10px] text-[#4a6984]/70 mt-1 font-medium">
              Leave blank to share anonymously.
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          type="submit"
          disabled={!canSubmit}
          data-testid="skills-generator-submit"
          className="ae-cta h-12 px-8 rounded-lg text-base inline-flex items-center gap-2 disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Generating…
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" /> Generate skill activity
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default SkillsGeneratorForm;
